import apiClient from './api';
import AuthService from './auth';

class WishlistService {
  static async getWishlist() {
    if (!AuthService.isAuthenticated()) {
      const items = this.loadLocalWishlist();
      return { success: true, wishlistItems: items };
    }
    try {
      const response = await apiClient.get('/cart/wishlist');
      return { success: true, wishlistItems: response.data };
    } catch (error) {
      console.error('Get wishlist error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch wishlist'
      };
    }
  }

  static async addToWishlist(productId) {
    if (!AuthService.isAuthenticated()) {
      // Guest mode: store locally (match existing local format)
      const items = this.loadLocalWishlist();
      if (!items.some((x) => (x.product_id || x.id) === productId)) {
        items.push({ id: productId, product_id: productId });
        this.saveLocalWishlist(items);
      }
      return { success: true, wishlistItems: items };
    }
    try {
      const response = await apiClient.post('/cart/wishlist', {
        product_id: productId
      });
      return { success: true, wishlistItem: response.data };
    } catch (error) {
      console.error('Add to wishlist error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to add item to wishlist'
      };
    }
  }

  static async removeFromWishlist(itemId) {
    if (!AuthService.isAuthenticated()) {
      const items = this.loadLocalWishlist().filter((x) => (x.id !== itemId && x.product_id !== itemId));
      this.saveLocalWishlist(items);
      return { success: true, wishlistItems: items };
    }
    try {
      await apiClient.delete(`/cart/wishlist/${itemId}`);
      return { success: true };
    } catch (error) {
      console.error('Remove from wishlist error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to remove item from wishlist'
      };
    }
  }

  static saveLocalWishlist(items) {
    localStorage.setItem('wishlist_items', JSON.stringify(items));
  }

  static loadLocalWishlist() {
    try {
      return JSON.parse(localStorage.getItem('wishlist_items') || '[]');
    } catch (error) {
      console.error('Error loading local wishlist:', error);
      return [];
    }
  }

  /**
   * Daraz-style move: add product to cart (merge quantity if already in cart), then remove wishlist row.
   * Returns { success, message } with message for toast: "Item moved to cart successfully!"
   */
  static async moveToCartFromWishlist(wishlistItem) {
    try {
      const CartService = (await import('./cart')).default;
      const product = wishlistItem?.product || wishlistItem;
      const pid = product?.id ?? product?._id ?? wishlistItem?.product_id;
      if (pid == null) {
        return { success: false, error: 'Invalid wishlist item' };
      }

      const productPayload =
        product && (product.id != null || product._id != null)
          ? product
          : { id: pid, product_id: pid, name: wishlistItem?.product?.name || 'Product', price: wishlistItem?.product?.price ?? 0 };

      const addRes = await CartService.addToCart(pid, 1, productPayload);
      if (!addRes.success) {
        return addRes;
      }

      const rowId = wishlistItem?.id;
      if (rowId == null) {
        return { success: false, error: 'Wishlist row id missing' };
      }

      const remRes = await this.removeFromWishlist(rowId);
      if (!remRes.success) {
        return {
          success: false,
          error: remRes.error || 'Added to cart but could not remove from wishlist',
          partial: true,
        };
      }

      return { success: true, message: 'Item moved to cart successfully!' };
    } catch (error) {
      console.error('moveToCartFromWishlist error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Move to cart failed',
      };
    }
  }
}

export default WishlistService;
